import { useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useNavigate } from "react-router";
import { Icon, Select } from "@astrum/ui";
import { TIMELINE_ZOOMS } from "@astrum/shared";
import type { TimelineGroup, TimelineZoom } from "@astrum/shared";
import type { Task } from "../../lib/api.js";
import {
  ZOOM_PX_PER_DAY,
  applyDrag,
  arrowPath,
  barRect,
  dayToX,
  dependencyConflict,
  dragDays,
  groupForTimeline,
  layoutTimelineRows,
  patchFromDrag,
  taskSpan,
  timelineRange,
  timelineTicks,
  todayDay,
} from "../../lib/work-views.js";
import type { DragMode, Span } from "../../lib/work-views.js";
import type { TaskViewProps } from "./types.js";
import "./task-views.css";

const LABEL_W = 220;
const HEAD_H = 34;
const ROW_H = 32;
const GROUP_H = 26;
const BAR_H = 20;

const ZOOM_LABEL: Record<TimelineZoom, string> = { week: "Week", month: "Month", quarter: "Quarter" };

type Drag = { taskId: string; mode: DragMode; days: number };

/**
 * A Gantt: one bar per task from its start to its due date, grouped by section
 * or project, with an arrow from each blocker to the task waiting on it.
 *
 * Everything is placed by absolute position from the date maths. There is no
 * element per day, so a two-year range costs the same DOM as a two-week one;
 * the grid lines are one repeating CSS gradient. Header ticks scale with the
 * zoom (days, weeks, months), never with the range's length in days.
 *
 * A blocker that ends after its dependant starts is drawn in warning colours,
 * on the arrow and on the dependant's bar.
 */
export function TimelineView({ tasks, canEdit, onPatch, state, onState }: TaskViewProps) {
  const navigate = useNavigate();
  const zoom = state.zoom;
  const ppd = ZOOM_PX_PER_DAY[zoom];
  const today = todayDay();

  const scheduled = useMemo(() => tasks.filter((t) => taskSpan(t)), [tasks]);
  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const spans = useMemo(() => new Map(scheduled.map((t) => [t.id, taskSpan(t)!])), [scheduled]);

  // "By section" with no sections anywhere would be a single "No section" band;
  // fall back to something that does say where the work is.
  const group: TimelineGroup = useMemo(() => {
    if (state.timelineGroup !== "section") return state.timelineGroup;
    if (scheduled.some((t) => t.sectionId)) return "section";
    return new Set(scheduled.map((t) => t.projectId)).size > 1 ? "project" : "none";
  }, [state.timelineGroup, scheduled]);

  const blocks = useMemo(() => groupForTimeline(scheduled, group), [scheduled, group]);
  const { rows, height, yOf } = useMemo(() => layoutTimelineRows(blocks, ROW_H, GROUP_H), [blocks]);

  const { origin, end } = useMemo(() => timelineRange([...spans.values()], today), [spans, today]);
  const canvasW = (end - origin + 1) * ppd;
  const ticks = useMemo(() => timelineTicks(origin, end, zoom, ppd), [origin, end, zoom, ppd]);

  /* ── drag ── */
  const [drag, setDrag] = useState<Drag | null>(null);
  const suppressClick = useRef(false);

  const spanOf = (task: Task): Span => {
    const base = spans.get(task.id)!;
    return drag && drag.taskId === task.id ? applyDrag(base, drag.mode, drag.days) : base;
  };

  const beginDrag = (event: ReactPointerEvent, task: Task, mode: DragMode) => {
    if (!canEdit(task) || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    let days = 0;
    let moved = false;
    const move = (e: PointerEvent) => {
      if (Math.abs(e.clientX - startX) > 3) moved = true;
      days = dragDays(e.clientX - startX, ppd);
      setDrag({ taskId: task.id, mode, days });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      setDrag(null);
      suppressClick.current = moved;
      if (days !== 0) {
        const next = applyDrag(spans.get(task.id)!, mode, days);
        onPatch(task, patchFromDrag(task, next, mode));
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  /* ── arrows ── */
  const arrows = useMemo(() => {
    const out: { key: string; d: string; conflict: boolean }[] = [];
    for (const task of scheduled) {
      const y2 = yOf.get(task.id);
      if (y2 === undefined) continue;
      const to = spanOf(task);
      for (const blockerId of task.blockedByIds ?? []) {
        const blocker = byId.get(blockerId);
        if (blocker && !spans.has(blockerId)) continue;
        const y1 = yOf.get(blockerId);
        if (!blocker || y1 === undefined) continue;
        const from = spanOf(blocker);
        const x1 = dayToX(from.end + 1, origin, ppd);
        const x2 = dayToX(to.start, origin, ppd);
        out.push({
          key: `${blockerId}>${task.id}`,
          d: arrowPath(x1, y1 + ROW_H / 2, x2, y2 + ROW_H / 2),
          conflict: dependencyConflict(from, to) && blocker.status !== "done" && blocker.status !== "cancelled",
        });
      }
    }
    return out;
    // spanOf reads drag; listed through it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduled, byId, yOf, origin, ppd, drag, spans]);
  const conflicted = useMemo(() => new Set(arrows.filter((a) => a.conflict).map((a) => a.key.split(">")[1]!)), [arrows]);

  /* Start scrolled to today, and again when zoom changes the scale. */
  const scrollRef = useRef<HTMLDivElement>(null);
  const jumpToToday = () => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = Math.max(0, dayToX(today, origin, ppd) - (el.clientWidth - LABEL_W) / 3);
  };
  useEffect(jumpToToday, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayX = dayToX(today, origin, ppd) + ppd / 2;
  const gridSize = zoom === "quarter" ? 30 * ppd : zoom === "month" ? 7 * ppd : ppd;

  return (
    <div className="tv-timeline">
      <div className="tv-toolbar">
        <div className="tv-zoom" role="group" aria-label="Zoom">
          {TIMELINE_ZOOMS.map((z) => (
            <button
              key={z}
              type="button"
              className={`tv-btn ${z === zoom ? "is-active" : ""}`}
              aria-pressed={z === zoom}
              onClick={() => onState({ zoom: z })}
            >
              {ZOOM_LABEL[z]}
            </button>
          ))}
        </div>
        <button type="button" className="tv-btn" onClick={jumpToToday}>
          Today
        </button>
        <label className="tv-toolbar__field">
          <span>Group by</span>
          <Select
            aria-label="Group timeline by"
            value={state.timelineGroup}
            onChange={(e) => onState({ timelineGroup: e.target.value as TimelineGroup })}
          >
            <option value="section">Section</option>
            <option value="project">Project</option>
            <option value="none">Nothing</option>
          </Select>
        </label>
        <span className="tv-legend">
          <span className="tv-legend__swatch" /> Blocker ends after the next task starts
        </span>
        <span className="tv-toolbar__count">
          {tasks.length - scheduled.length > 0 ? `${tasks.length - scheduled.length} without dates not shown` : ""}
        </span>
      </div>

      {scheduled.length === 0 ? (
        <p className="tv-empty">No task here has a start or due date yet.</p>
      ) : (
        <div className="tv-timeline__scroll" ref={scrollRef}>
          <div className="tv-timeline__inner" style={{ width: LABEL_W + canvasW, height: HEAD_H + height }}>
            {/* Header: sticky to the top; ticks are placed, not laid out. */}
            <div className="tv-timeline__head" style={{ height: HEAD_H }}>
              <div className="tv-timeline__corner" style={{ width: LABEL_W }} />
              <div className="tv-timeline__ticks" style={{ left: LABEL_W, width: canvasW }}>
                {ticks.map((tick) => (
                  <span key={tick.day} className={`tv-tick ${tick.major ? "is-major" : ""}`} style={{ left: tick.x }}>
                    {tick.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Labels: sticky to the left. */}
            <div className="tv-timeline__labels" style={{ width: LABEL_W, top: HEAD_H, height }}>
              {rows.map((row) =>
                row.kind === "header" ? (
                  <div key={row.key} className="tv-group-label" style={{ top: row.y, height: GROUP_H }}>
                    {row.label}
                  </div>
                ) : (
                  <button
                    key={row.key}
                    type="button"
                    className="tv-row-label"
                    style={{ top: row.y, height: ROW_H }}
                    onClick={() => navigate(`/work/task/${row.taskId}`)}
                    title={byId.get(row.taskId)?.title}
                  >
                    {byId.get(row.taskId)?.title}
                  </button>
                ),
              )}
            </div>

            <div
              className="tv-timeline__canvas"
              style={{
                left: LABEL_W,
                top: HEAD_H,
                width: canvasW,
                height,
                backgroundSize: `${gridSize}px 100%`,
              }}
            >
              {rows.map((row) =>
                row.kind === "header" ? (
                  <div key={row.key} className="tv-group-band" style={{ top: row.y, height: GROUP_H }} />
                ) : null,
              )}

              <svg className="tv-arrows" width={canvasW} height={height} aria-hidden="true">
                <defs>
                  <marker id="tv-arrowhead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 z" fill="currentColor" />
                  </marker>
                </defs>
                {arrows.map((a) => (
                  <path key={a.key} d={a.d} className={`tv-arrow ${a.conflict ? "is-conflict" : ""}`} markerEnd="url(#tv-arrowhead)" />
                ))}
              </svg>

              {rows.map((row) => {
                if (row.kind !== "task") return null;
                const task = byId.get(row.taskId)!;
                const rect = barRect(spanOf(task), origin, ppd);
                const editable = canEdit(task);
                return (
                  <div
                    key={row.key}
                    className={[
                      "tv-bar",
                      task.status === "done" ? "is-done" : "",
                      conflicted.has(task.id) ? "is-conflict" : "",
                      editable ? "is-editable" : "",
                      drag?.taskId === task.id ? "is-dragging" : "",
                    ].join(" ")}
                    style={{ left: rect.left, width: rect.width, top: row.y + (ROW_H - BAR_H) / 2, height: BAR_H }}
                    onPointerDown={(e) => beginDrag(e, task, "move")}
                    onClick={() => {
                      if (suppressClick.current) {
                        suppressClick.current = false;
                        return;
                      }
                      navigate(`/work/task/${task.id}`);
                    }}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/work/task/${task.id}`)}
                    aria-label={`${task.title}${conflicted.has(task.id) ? " (starts before a blocker ends)" : ""}`}
                    title={task.title}
                  >
                    {editable && (
                      <span className="tv-bar__handle tv-bar__handle--start" onPointerDown={(e) => beginDrag(e, task, "start")} />
                    )}
                    <span className="tv-bar__title">{rect.width > 60 ? task.title : ""}</span>
                    {editable && (
                      <span className="tv-bar__handle tv-bar__handle--end" onPointerDown={(e) => beginDrag(e, task, "end")} />
                    )}
                  </div>
                );
              })}

              <div className="tv-today" style={{ left: todayX }} aria-hidden="true">
                <span>Today</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {scheduled.some(canEdit) && (
        <p className="tv-note">
          <Icon name="calendar" size={12} /> Drag a bar to move it, or its ends to change the dates.
        </p>
      )}
    </div>
  );
}
